import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
  TextField
} from '@material-ui/core';
import Fetcher, { URLBuild } from '../../services/fetcher';
import settings from '../../settings';
import { useAuthentication } from '../../contexts/Authentication';

interface UserCreateProps extends DialogProps {
  onRequestClose: (updated: boolean) => void
};

const useStyle = makeStyles({
  container: {
    width: 600
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around'
  },
  singleInput: {
    width: 250
  }
});

const UserCreate = (props: UserCreateProps) => {
  const classes = useStyle();
  const auth = useAuthentication();
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [email, setEmail] = useState<string>('');
  const [emailConfirm, setEmailConfirm] = useState<string>('');
  const [validEmail, setValidEmail] = useState<boolean>(true);
  const [validEmailConfirm, setValidEmailConfirm] = useState<boolean>(true);
  const [helper, setHelper] = useState<string>('');
  const [helperConfirm, setHelperConfirm] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [validPassword, setValidPassword] = useState<boolean>(true);
  const [validPasswordConfirm, setValidPasswordConfirm] = useState<boolean>(true);
  const [helperPw, setHelperPw] = useState<string>('');
  const [helperConfirmPw, setHelperConfirmPw] = useState<string>('');

  const validateEmail = (emailToCheck: string): boolean => {
    const re = /^(([^<>()[\]\\.,;:\s@]+(\.[^<>()[\]\\.,;:\s@]+)*)|(.+))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(emailToCheck);
  }

  const checkEmail = (): Promise<boolean> => {
    if (!validateEmail(email)) {
      setValidEmail(false);
      setHelper('Adresse Invalide');
      return Promise.resolve(false);
    }
    if (email !== emailConfirm) {
      setValidEmailConfirm(false);
      setHelperConfirm('Les adresses doivent être identiques');
      return Promise.resolve(false);
    }
    return Fetcher.get(URLBuild(settings.OpenIdProvider.users, {start: 0, end: 1, filter: email}))
    .then((data: any) => {
      if (data.count === 0) {
        return true;
      }
      setValidEmail(false);
      setHelper('Utilisateur existant');
      return false;
    })
    .catch(() => {
      auth.logout();
      setHelper('Déconnecté');
      setHelperConfirm('Déconnecté');
      return false;
    });
  };

  const checkPassword = (): boolean => {
    if (password.length < 6) {
      setValidPassword(false);
      setHelperPw('Mot de passe < 6 caractère');
      return false;
    }
    if (password !== passwordConfirm) {
      setValidPasswordConfirm(false);
      setHelperConfirmPw('Les mots de passe doivent être identiques');
      return false;
    }
    return true;
  };

  const handlePrev = () => {
    if (stepIndex !== 1)
      return;
    setStepIndex(0);
    setPassword('');
    setPasswordConfirm('');
    setValidPassword(true);
    setValidPasswordConfirm(true);
    setHelperPw('');
    setHelperConfirmPw('');
  };

  const handleNext = () => {
    if (stepIndex === 0) {
      checkEmail().then((status: boolean) => {
        if (status === true)
          setStepIndex(1);
      })
    } else if (stepIndex === 1) {
      if (checkPassword()) {
        Fetcher.post(URLBuild(settings.OpenIdProvider.users, {}), JSON.stringify({email, password}))
        .then(() => props.onRequestClose(true))
        .catch((e) => {
          console.error(e);
          setValidPassword(false);
          setHelperPw('Error: please contact an admin.');
        });
      }
    }
  };

  const onEmailChange = (event: React.ChangeEvent<{value: string}>) => {
    setEmail(event.target.value)
    setValidEmail(true);
    setValidEmailConfirm(true);
    setHelper('');
    setHelperConfirm('');
  };

  const onEmailConfirmChange = (event: React.ChangeEvent<{value: string}>) => {
    setEmailConfirm(event.target.value)
    setValidEmail(true);
    setValidEmailConfirm(true);
    setHelper('');
    setHelperConfirm('');
  };

  const onPasswordChange = (event: React.ChangeEvent<{value: string}>) => {
    setPassword(event.target.value)
    setValidPassword(true);
    setValidPasswordConfirm(true);
    setHelperPw('');
    setHelperConfirmPw('');
  };

  const onPasswordConfirmChange = (event: React.ChangeEvent<{value: string}>) => {
    setPasswordConfirm(event.target.value)
    setValidPassword(true);
    setValidPasswordConfirm(true);
    setHelperPw('');
    setHelperConfirmPw('');
  };

  const submit = (event: any) => {
    if (event.key === 'Enter') {
      if (!validEmail || !validEmailConfirm || !validPassword || !validPasswordConfirm)
        return;
      handleNext();
    }
  };

  const getStepContent = () => {
    switch (stepIndex) {
      case 0:
        return (
          <div className={classes.inputContainer}>
            <TextField
              autoFocus
              id="email"
              key="email"
              className={classes.singleInput}
              error={!validEmail}
              label="Adresse e-mail"
              type="email"
              value={email}
              onChange={onEmailChange}
              helperText={helper}
              onKeyPress={submit}
            />
            <TextField
              id="emailConfirm"
              key="emailConfirm"
              className={classes.singleInput}
              error={!validEmailConfirm}
              label="Confirmer e-mail"
              type="email"
              value={emailConfirm}
              onChange={onEmailConfirmChange}
              helperText={helperConfirm}
              onKeyPress={submit}
            />
          </div>
        );
      case 1:
        return (
          <div className={classes.inputContainer}>
            <TextField
              autoFocus
              id="password"
              key="password"
              error={!validPassword}
              helperText={helperPw}
              className={classes.singleInput}
              label="Mot de passe"
              type="password"
              value={password}
              onChange={onPasswordChange}
              onKeyPress={submit}
            />
            <TextField
              id="passwordConfirm"
              key="passwordConfirm"
              error={!validPasswordConfirm}
              helperText={helperConfirmPw}
              className={classes.singleInput}
              label="Confirmer mot de passe"
              type="password"
              value={passwordConfirm}
              onChange={onPasswordConfirmChange}
              onKeyPress={submit}
            />
          </div>
        );
    }
  }

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>
        Création d'un nouvel utilisateur
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.container}>
        <Stepper activeStep={stepIndex}>
          <Step>
            <StepLabel>Adresse e-mail</StepLabel>
          </Step>
          <Step>
            <StepLabel>Mot de passe</StepLabel>
          </Step>
        </Stepper>
        {getStepContent()}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handlePrev}
          disabled={stepIndex < 1}
        >
          Précédent
        </Button>
        <Button
          onClick={handleNext}
          disabled={!validEmail || !validEmailConfirm || !validPassword || !validPasswordConfirm}
          variant="contained"
        >
          {stepIndex < 1 ? 'Suivant' : 'Terminer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserCreate;